// ============================================================
// VCS Helpers — Globale Hilfsfunktionen
// ============================================================
// Winzige Datei, wird als Erste geladen (vor state.js und allen
// Klassen). Nur Funktionen die wirklich in jedem Modul gebraucht
// werden und keinen eigenen Namespace rechtfertigen.
// ============================================================

// t(key) — i18n-Shortcut, delegiert an VCS.i18n.t()
// Vor dem Init (VCS.i18n noch nicht instanziiert) greift direkt auf TRANSLATIONS zurück.
function t(key) {
    if (window.VCS?.i18n) {
        return VCS.i18n.t(key);
    }
    // Fallback vor VCS.I18n-Init
    const lang = window.VCS?.state?.currentLang || 'de';
    return TRANSLATIONS?.[lang]?.[key] || TRANSLATIONS?.de?.[key] || key;
}

// escapeHtml(value) — HTML-Sonderzeichen escapen
function escapeHtml(value) {
    return String(value)
        .replaceAll('&',  '&amp;')
        .replaceAll('<',  '&lt;')
        .replaceAll('>',  '&gt;')
        .replaceAll('"',  '&quot;')
        .replaceAll("'",  '&#39;');
}

// linkAnchor(escapedUrl) — <a>-Tag fuer eine bereits escapte URL
// rel=noopener noreferrer: verhindert Zugriff der Zielseite auf window.opener
function linkAnchor(escapedUrl) {
    return `<a href="${escapedUrl}" class="chat-link" target="_blank" rel="noopener noreferrer">${escapedUrl}</a>`;
}

// trimUrlTail(url) — Satzzeichen und unbalancierte Klammern am URL-Ende abschneiden
// Liefert [url, tail] — tail gehoert zum Text, nicht zum Link.
function trimUrlTail(url) {
    let tail = '';

    const punct = url.match(/[.,;:!?]+$/);
    if (punct) {
        tail = punct[0];
        url  = url.slice(0, -punct[0].length);
    }

    // "(siehe https://x.de/a)" — die letzte Klammer schliesst den Satz, nicht die URL
    while (url.endsWith(')') && (url.split(')').length > url.split('(').length)) {
        url  = url.slice(0, -1);
        tail = ')' + tail;
    }

    return [url, tail];
}

// linkify(value) — rohen Text escapen und http/https-URLs klickbar machen
// Zerlegt in URL- und Text-Segmente und escapt jedes einzeln. Nur http/https
// werden gematcht — javascript:-URLs bleiben damit reiner Text.
function linkify(value) {
    const text  = String(value);
    const rxUrl = /https?:\/\/[^\s<>"']+/gi;

    let html = '';
    let last = 0;
    let match;

    while ((match = rxUrl.exec(text)) !== null) {
        const [url, tail] = trimUrlTail(match[0]);
        if (!url) continue;

        html += escapeHtml(text.slice(last, match.index));
        html += linkAnchor(escapeHtml(url));

        // tail bleibt liegen und landet im naechsten Text-Segment
        last = match.index + url.length;
    }

    return html + escapeHtml(text.slice(last));
}

// escapeHtmlWithBreaks(value) — wie escapeHtml + \n → <br>, URLs werden verlinkt
function escapeHtmlWithBreaks(value) {
    return linkify(value).replaceAll('\n', '<br>');
}

// parseMarkdown(value) — Markdown zu HTML (fuer Bot-Nachrichten)
function parseMarkdown(value) {
    const escaped = escapeHtml(value);
    const lines = escaped.split('\n');
    let html = '';
    let inList = false;

    for (let i = 0; i < lines.length; i++) {
        let line = lines[i];

        // Headings: ### Text
        const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
        if (headingMatch) {
            if (inList) { html += '</ul>'; inList = false; }
            const level = headingMatch[1].length;
            html += `<h${level} class="md-heading">${applyInline(headingMatch[2])}</h${level}>`;
            continue;
        }

        // List items: - Text oder  - Text (eingerueckt)
        const listMatch = line.match(/^(\s*)[-*]\s+(.+)$/);
        if (listMatch) {
            if (!inList) { html += '<ul class="md-list">'; inList = true; }
            html += `<li>${applyInline(listMatch[2])}</li>`;
            continue;
        }

        // Leerzeile
        if (!line.trim()) {
            if (inList) { html += '</ul>'; inList = false; }
            html += '<br>';
            continue;
        }

        // Normale Zeile
        if (inList) { html += '</ul>'; inList = false; }
        html += applyInline(line) + '<br>';
    }

    if (inList) html += '</ul>';

    // Trailing <br> entfernen
    html = html.replace(/(<br>)+$/, '');

    return html;
}

// Inline-Markdown: **fett**, *kursiv*, `code`, Links
// text ist hier bereits escapt (parseMarkdown escapt vorab), darum kein escapeHtml.
// Die Link-Ersetzung laeuft zuletzt, damit die erzeugten Tags nicht mehr angefasst werden.
function applyInline(text) {
    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code class="md-code">$1</code>')
        .replace(/https?:\/\/[^\s<]+/gi, (raw) => {
            const [url, tail] = trimUrlTail(raw);
            return url ? linkAnchor(url) + tail : raw;
        });
}

// stripMarkdown(value) — MD-Syntax entfernen fuer Vorschau-Texte
function stripMarkdown(value) {
    return String(value)
        .replace(/^#{1,4}\s+/gm, '')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .replace(/^[\s]*[-*]\s+/gm, '- ')
        .replace(/\n{2,}/g, '\n')
        .trim();
}

// extractYouTubeId(text) — YouTube-Video-ID aus URL extrahieren
function extractYouTubeId(text) {
    if (!text) {
        return null;
    }
    const m = text.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    return m ? m[1] : null;
}

// extractGoogleMapsUrl(text) — Google-Maps-URL aus Text extrahieren
function extractGoogleMapsUrl(text) {
    if (!text) {
        return null;
    }
    const m = text.match(
        /https?:\/\/(?:(?:www\.)?google\.[a-z.]+\/maps[^\s<>"')\]]*|maps\.google\.[a-z.]+\/[^\s<>"')\]]*|goo\.gl\/maps\/[\w-]+|maps\.app\.goo\.gl\/[\w-]+)/i
    );
    return m ? m[0] : null;
}

// extractFirstUrl(text) — Erste URL aus Text extrahieren
function extractFirstUrl(text) {
    if (!text) {
        return null;
    }
    const m = text.match(/https?:\/\/[^\s<>"')\]]+/i);
    return m ? m[0] : null;
}
